import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import cn from 'classnames';
// API
import {
  ACCEPT_ANY_AI_CLIP_CANDIDATE,
  REJECT_ANY_AI_CLIP_CANDIDATE,
} from 'api/interview/mutations';
import { CREATE_UNREGISTERED_CONTACT } from 'api/mentions/mutations';
import { GET_NOT_REVIEWED_ANY_AI_CLIP_CANDIDATES } from 'api/interview/queries';
import { GET_MEDIA_POSTS } from 'api/mediaPost/queries';
// Types
import { GetNotReviewedAnyAiClipCandidates_getNotReviewedAnyAiClipCandidates } from 'api/interview/types/GetNotReviewedAnyAiClipCandidates';
import {
  AcceptAnyAiClipCandidate,
  AcceptAnyAiClipCandidateVariables,
} from 'api/interview/types/AcceptAnyAiClipCandidate';
import {
  RejectAnyAiClipCandidate,
  RejectAnyAiClipCandidateVariables,
} from 'api/interview/types/RejectAnyAiClipCandidate';
import {
  CreateUnregisteredContact,
  CreateUnregisteredContactVariables,
} from 'api/mentions/types/CreateUnregisteredContact';
import { GetMediaPostsInput } from 'api/graphql-global-types';
import { Mention } from 'ui3/Mentions/Mentions';
// Helpers
import { formatMentionsInput } from 'helpers/mentions';
import { getAiClipExcerpt, getAiClipTitle } from 'helpers/aiClips';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Ui
import VideoPlayer from 'ui/VideoPlayer/VideoPlayer';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
import Input from 'ui3/Input/Input';
import TextArea from 'ui3/TextArea/TextArea';
import TagsInput from 'ui3/TagsInput/TagsInput';
import Mentions from 'ui3/Mentions/Mentions';
// Styles
import styles from './AiClipReviewPanel.module.scss';

export type AiClipReviewPanelProps = {
  storeId: string;
  candidate: GetNotReviewedAnyAiClipCandidates_getNotReviewedAnyAiClipCandidates;
  /** 1-based position used for the "AI Clip #N" label. */
  index: number;
  /** Apollo keys cache entries by the full variable shape, so a refetch must match it exactly. Omit to skip the refetch. */
  mediaPostsQueryInput?: GetMediaPostsInput;
  /** Fired after a successful approve/reject, with the reviewed candidate id. */
  onReviewed?: (candidateId: string) => void;
  className?: string;
};

const AiClipReviewPanel = ({
  storeId,
  candidate,
  index,
  mediaPostsQueryInput,
  onReviewed,
  className,
}: AiClipReviewPanelProps): JSX.Element => {
  const defaultTitle = getAiClipTitle(candidate, index);
  const defaultBody = getAiClipExcerpt(candidate);

  const [title, setTitle] = useState<string>(defaultTitle);
  const [body, setBody] = useState<string>(defaultBody);
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [isEditView, setIsEditView] = useState<boolean>(false);

  const [acceptCandidate, { loading: acceptLoading }] = useMutation<
    AcceptAnyAiClipCandidate,
    AcceptAnyAiClipCandidateVariables
  >(ACCEPT_ANY_AI_CLIP_CANDIDATE);

  const [rejectCandidate, { loading: rejectLoading }] = useMutation<
    RejectAnyAiClipCandidate,
    RejectAnyAiClipCandidateVariables
  >(REJECT_ANY_AI_CLIP_CANDIDATE);

  const [createUnregisteredContact] = useMutation<
    CreateUnregisteredContact,
    CreateUnregisteredContactVariables
  >(CREATE_UNREGISTERED_CONTACT);

  const isLoading = acceptLoading || rejectLoading;

  useEffect(() => {
    setTitle(getAiClipTitle(candidate, index));
    setBody(getAiClipExcerpt(candidate));
    setHashtags([]);
    setMentions([]);
    setIsEditView(false);
  }, [candidate, index]);

  const refetchQueries = [
    {
      query: GET_NOT_REVIEWED_ANY_AI_CLIP_CANDIDATES,
      variables: { input: { ownerId: storeId } },
    },
    ...(mediaPostsQueryInput
      ? [{ query: GET_MEDIA_POSTS, variables: { input: mediaPostsQueryInput } }]
      : []),
  ];

  const formatMentionsForAiClip = async (mentionsValues: Mention[]) => {
    const createUnregisteredContactInput =
      ((mentionsValues?.filter(
        (mention) => !mention.targetType
      ) as unknown) as CreateUnregisteredContactVariables['input']) || [];

    if (createUnregisteredContactInput.length > 0) {
      const unregisterContactsData = await createUnregisteredContact({
        variables: { input: createUnregisteredContactInput },
      });

      const newUnregisteredContacts =
        unregisterContactsData.data?.createUnregisteredContact?.map(
          (contact) => ({ unregisteredContactId: contact.id })
        ) || [];

      const oldMentions =
        mentionsValues?.filter((mention) => mention?.targetType) || [];
      const formatedOldMentions = formatMentionsInput(oldMentions);

      return [...formatedOldMentions, ...newUnregisteredContacts];
    }

    const oldMentions =
      mentionsValues?.filter((mention) => mention?.targetType) || [];

    return formatMentionsInput(oldMentions);
  };

  const handleHashtagsChange = (tags) => {
    setHashtags(tags);
  };

  const handleToggleEditView = () => {
    setIsEditView(!isEditView);
  };

  const formatHashtagsInput = (hashtagValues: string[]) =>
    hashtagValues?.length
      ? hashtagValues.map((hashtag) => ({ name: hashtag }))
      : null;

  const handleApprove = async () => {
    try {
      const mentionsInputs = await formatMentionsForAiClip(mentions);
      const hashtagInputs = formatHashtagsInput(hashtags);

      await acceptCandidate({
        variables: {
          input: {
            candidateId: candidate.id,
            title: title || undefined,
            body: body || undefined,
            hashtagInputs: hashtagInputs || undefined,
            mentionsInputs:
              mentionsInputs?.length > 0 ? mentionsInputs : undefined,
          },
        },
        refetchQueries,
      });
      showToast({
        message: `You've successfully approved the AI clip.`,
        type: 'success',
      });
      onReviewed?.(candidate.id);
    } catch (error) {
      showToast({
        message: error?.message || 'Failed to approve AI clip',
        type: 'error',
      });
    }
  };

  const handleReject = async () => {
    try {
      await rejectCandidate({
        variables: { input: { candidateId: candidate.id } },
        refetchQueries,
      });
      showToast({
        message: `You've successfully rejected the AI clip.`,
        type: 'success',
      });
      onReviewed?.(candidate.id);
    } catch (error) {
      showToast({
        message: error?.message || 'Failed to reject AI clip',
        type: 'error',
      });
    }
  };

  const topic = candidate.topic?.trim();

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.videoColumn}>
        {candidate.clipUrl ? (
          <VideoPlayer
            wrapperClassName={styles.videoPlayerWrapper}
            videoClassName={styles.videoPlayer}
            autoPlay={false}
            src={candidate.clipUrl}
            volumeControl="speaker"
            poster={candidate.thumbnailUrl || ''}
          />
        ) : (
          <div className={styles.videoPlaceholder}>
            <Text variant="body2Regular" color="lights-low">
              This clip is still processing.
            </Text>
          </div>
        )}
      </div>

      <div className={styles.contentColumn}>
        {!isEditView ? (
          <>
            <div className={styles.info}>
              <Text
                variant="overlineMedium"
                color="lights-low"
                className={styles.eyebrow}
              >
                AI Clip #{index}
              </Text>
              <Text
                variant="subtitle1"
                color="lights-high"
                className={styles.title}
              >
                {defaultTitle}
              </Text>

              {defaultBody && (
                <Text
                  variant="body2Regular"
                  color="lights-low"
                  className={styles.excerpt}
                >
                  {defaultBody}
                </Text>
              )}

              {topic && (
                <div className={styles.topicRow}>
                  <Text variant="subtitle3" color="lights-medium">
                    Topic
                  </Text>
                  <Text variant="body2Regular" color="lights-low">
                    {topic}
                  </Text>
                </div>
              )}

              <Text
                variant="body4Regular"
                color="lights-dim"
                className={styles.hint}
              >
                Approving publishes it to your media feed.
              </Text>
            </div>

            <div className={styles.actions}>
              <Button
                variant="secondary"
                size="medium"
                onClick={handleToggleEditView}
                disabled={isLoading}
                icon="pencil"
                className={styles.editButton}
              >
                Edit
              </Button>
              <div className={styles.decisionButtons}>
                <Button
                  variant="success"
                  size="medium"
                  onClick={handleApprove}
                  loading={isLoading}
                  className={styles.decisionButton}
                >
                  Approve
                </Button>
                <Button
                  variant="error"
                  size="medium"
                  onClick={handleReject}
                  disabled={isLoading}
                  className={styles.decisionButton}
                >
                  Reject
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.form}>
              <Input
                name="title"
                label="Title"
                placeholder="Enter title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={styles.inputField}
              />

              <TextArea
                name="body"
                label="Description"
                placeholder="Enter description"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                className={styles.inputField}
              />

              <div className={styles.inputField}>
                <TagsInput
                  tags={hashtags}
                  label="Tagging"
                  onChange={handleHashtagsChange}
                  name="hashtags"
                />
              </div>

              <div className={styles.inputField}>
                <Mentions mentions={mentions} onChange={setMentions} />
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                variant="secondary"
                size="medium"
                onClick={handleToggleEditView}
                disabled={isLoading}
                className={styles.editButton}
              >
                Cancel
              </Button>
              <div className={styles.decisionButtons}>
                <Button
                  variant="success"
                  size="medium"
                  onClick={handleApprove}
                  loading={isLoading}
                  className={styles.decisionButton}
                >
                  Approve
                </Button>
                <Button
                  variant="error"
                  size="medium"
                  onClick={handleReject}
                  disabled={isLoading}
                  className={styles.decisionButton}
                >
                  Reject
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AiClipReviewPanel;
